<script setup>
import {useReservationsStore, useRestaurantsStore, useUsersStore, useInstantBookingSlotsStore} from "@/stores"
import moment from "moment-timezone"
import {numberWithCommas} from "@/plugins/misc"
import CmsText from "@/@core/components/CmsText.vue"

const reservationsStore = useReservationsStore()
let cancelingReservation = null;
({ cancelingReservation } = storeToRefs(reservationsStore))


const restaurantsStore = useRestaurantsStore()
let restaurants = null;
({ restaurants } = storeToRefs(restaurantsStore))

const usersStore = useUsersStore()
const instantBookingSlotsStore = useInstantBookingSlotsStore()

// State for instant booking cancellation
const instantBookingSlot = ref(null)
const showInstantBookingDialog = ref(false)

// RM-164: required reason for cancelling, shared with the restaurant.
const cancellationReason = ref('')

watch(() => cancelingReservation.value, async (newVal, oldVal) => {
  if (newVal !== oldVal) {
    usersStore.showLoadingOverlay()
    if(cancelingReservation.value){
      reservationsStore.getReservationById(cancelingReservation.value._id)

      // Check if this is an instant booking
      if (cancelingReservation.value.reservationType === 'instant' && cancelingReservation.value.instantBookingSlotId) {
        try {
          // Fetch the slot data for the instant booking dialog
          const slot = await instantBookingSlotsStore.fetchSlotById(cancelingReservation.value.instantBookingSlotId)
          instantBookingSlot.value = slot
          showInstantBookingDialog.value = true
        } catch (error) {
          console.error('Error fetching instant booking slot:', error)

          // Fall back to normal dialog if we can't fetch the slot
        }
      }
    }
    restaurantsStore.getRestaurants()
    usersStore.hideLoadingOverlay()
  }
}, { deep: true })  // the { deep: true } option ensures that we are watching nested properties too


const cancelReservation = () => {
  // RM-164: reason is required before the cancel can be confirmed.
  if (!cancellationReason.value.trim()) return
  reservationsStore.setReservationStatus(cancelingReservation.value._id, 'Canceled', cancellationReason.value.trim())
  close()
}

const close = () => {
  cancelingReservation.value = null
  instantBookingSlot.value = null
  showInstantBookingDialog.value = false
  cancellationReason.value = ''
}

// Handle instant booking cancellation completion
const handleInstantBookingCancellationComplete = result => {
  console.log('Instant booking cancellation complete:', result)

  // Refresh reservations to reflect the cancellation
  reservationsStore.getMyReservations()
  close()
}

const theRestaurant = computed(() => {
  if(cancelingReservation.value){
    return restaurants.value.find( restaurant =>(restaurant.name == cancelingReservation.value.confirmedRestaurant && restaurant.market == cancelingReservation.value.market) )
  }
  
  return null
})


const canCancelWithoutPenalty = computed(() => {
  // Add null/undefined checks before accessing properties
  if (!cancelingReservation.value ||
      !cancelingReservation.value.date ||
      !cancelingReservation.value.confirmedTime ||
      !cancelingReservation.value.timeZoneIdentifier ||
      !cancelingReservation.value.stripeHoldCancellationLimitHours) {
    return true // Default to allowing cancellation if data is incomplete
  }

  let reservationMoment = moment.tz(cancelingReservation.value.date + ' ' + cancelingReservation.value.confirmedTime, 'YYYY-MM-DD h:mma', cancelingReservation.value.timeZoneIdentifier)
  let nowMoment = moment.tz(cancelingReservation.value.timeZoneIdentifier)
  if(reservationMoment.subtract(cancelingReservation.value.stripeHoldCancellationLimitHours, 'hours').isBefore(nowMoment)){
    return false
  }
  
  return true
})


// Whether a cancel action is actually offered (mirrors the VCardActions v-if).
// The warning + required reason are only shown on these cancellable paths.
const canCancel = computed(() => {
  const r = cancelingReservation.value
  if (!r) return false

  return (
    (r.stripeHoldMaxAmount && r.stripeHoldConfirmedAmount)
    || (r.stripeHoldMaxAmount && canCancelWithoutPenalty.value)
    || theRestaurant.value?.generalReservationAllowCancellation
  )
})


const showDialog = computed(() => {
  // Don't show the normal dialog for instant bookings - they use InstantBookingCancellationDialog
  if(cancelingReservation.value && cancelingReservation.value.reservationType !== 'instant'){
    return true
  }

  return false
})
</script>

<template>
  <!-- 👉 Confirm Dialog -->
  <VDialog
    max-width="500"
    :model-value="showDialog"
  >
    <VCard class="text-center px-10 py-6">
      <VCardText>
        <VBtn
          icon
          variant="outlined"
          color="primary"
          class="mb-4"
          style="pointer-events: none;"
        >
          <span class="text-5xl">!</span>
        </VBtn>

        <template v-if="cancelingReservation?.stripeHoldMaxAmount && cancelingReservation?.stripeHoldConfirmedAmount">
          <h6
            v-if="canCancelWithoutPenalty"
            class="text-lg font-weight-medium"
          >
            Are you sure you want to cancel this reservation?
          </h6>
          <h6
            v-else
            class="text-lg font-weight-medium"
          >
            It is less than {{ cancelingReservation.stripeHoldCancellationLimitHours }} hours before the reservation start time - you may be charged the ${{ numberWithCommas(cancelingReservation?.stripeHoldConfirmedAmount.toFixed(2)) }} hold if you cancel. Are you sure?
          </h6>
        </template>
        <template v-else-if="cancelingReservation?.stripeHoldMaxAmount">
          <!--          case where hold was required but was not taken -->
          <h6
            v-if="canCancelWithoutPenalty"
            class="text-lg font-weight-medium"
          >
            Are you sure you want to cancel this reservation?
          </h6>
          <h6
            v-else
            class="text-lg font-weight-medium"
          >
            Please contact {{ theRestaurant?.name }} directly to cancel this reservation.
          </h6>
        </template>
        <template v-else>
          <h6
            v-if="theRestaurant?.generalReservationAllowCancellation"
            class="text-lg font-weight-medium"
          >
            Are you sure you want to cancel this reservation?
          </h6>
          <h6
            v-else
            class="text-lg font-weight-medium"
          >
            Please contact {{ theRestaurant?.name }} directly to cancel this reservation.
          </h6>
        </template>
      </VCardText>

      <!-- RM-164: "think twice" warning + required reason (shared with restaurant) -->
      <VCardText
        v-if="canCancel"
        class="pt-0 text-start"
      >
        <CmsText
          k="reservation.cancellation.warning"
          tag="p"
          class="text-body-2 text-medium-emphasis mb-4"
          :vars="{ restaurant: theRestaurant?.name || 'the restaurant' }"
        />
        <VTextarea
          v-model="cancellationReason"
          label="Reason for cancelling"
          placeholder="Let the restaurant know why you need to cancel"
          rows="3"
          auto-grow
          counter
          :rules="[v => !!(v && v.trim()) || 'A reason is required']"
          style="font-size: 16px;"
        />
      </VCardText>

      <VCardActions
        v-if="(cancelingReservation?.stripeHoldMaxAmount && cancelingReservation?.stripeHoldConfirmedAmount) || (cancelingReservation?.stripeHoldMaxAmount && canCancelWithoutPenalty) || theRestaurant?.generalReservationAllowCancellation"
        class="align-center justify-center gap-2"
      >
        <VBtn
          variant="elevated"
          :disabled="!cancellationReason.trim()"
          @click="cancelReservation"
        >
          Yes
        </VBtn>

        <VBtn
          color="secondary"
          variant="tonal"
          @click="close"
        >
          No
        </VBtn>
      </VCardActions>
      <VCardActions
        v-else
        class="align-center justify-center gap-2"
      >
        <VBtn
          color="secondary"
          variant="tonal"
          @click="close"
        >
          Okay
        </VBtn>
      </VCardActions>
    </VCard>
    <VBtn
      icon="$close"
      size="large"
      variant="text"
      color="secondary"
      class="dialog-close-btn"
      @click="close"
    >
      <VIcon>mdi-close</VIcon>
    </VBtn>
  </VDialog>

  <!-- Instant Booking Cancellation Dialog -->
  <InstantBookingCancellationDialog
    v-if="instantBookingSlot && cancelingReservation"
    :slot="instantBookingSlot"
    v-model="showInstantBookingDialog"
    :reservation="cancelingReservation"
    @update:model-value="(val) => { if (!val) close() }"
    @cancellation-complete="handleInstantBookingCancellationComplete"
  />
</template>

<style scoped>
.dialog-close-btn {
  position: fixed;
  right: 6px;
  top: 6px;
}
</style>
